// Lógica del histórico de informes (process-logs), separada de la vista.
// Las llamadas axios crudas viven en ../api/index.js; aquí se validan (success/error),
// se transforman (parse del payload) y se expone un helper puro para el realtime.
// Sin Vue ni `this`: la vista (views/history-reports.vue) solo orquesta estado/notificaciones.
import {
  fetchProcessLogs,
  fetchProcessLog,
  deleteProcessLog,
  downloadProcessLog,
} from '../api/index'

// Lista de procesos del histórico para un proyecto (type detailed_report).
// Mantiene los mismos params que enviaba la vista (el backend hoy solo usa project_id/type).
export async function fetchHistory(projectId, { orderField = 'name', orderDir = 'asc', page = 1, perPage = 20 } = {}) {
  const data = await fetchProcessLogs({
    project_id: projectId,
    type: 'detailed_report',
    order_field: orderField,
    order_dir: orderDir,
    page,
    per_page: perPage,
  })
  if (!data || !data.success) throw new Error((data && data.error) || 'error_retrieving_data')
  return data.process
}

// Payload guardado de un informe, para "Regenerar/Reutilizar" (read($id) trae el proceso).
export async function fetchReportPayload(id) {
  const data = await fetchProcessLog(id)
  if (!data || !data.success) throw new Error((data && data.error) || 'error_retrieving_data')
  return JSON.parse(data.process.payload)
}

// Borra un proceso. El backend valida ProcesslogPolicy@delete (403 si no es tuyo); en ese
// caso axios rechaza y el error lleva `response.data.error`, que la vista muestra.
export async function deleteLog(id) {
  const data = await deleteProcessLog(id)
  if (!data || !data.success) throw new Error((data && data.error) || 'error_deleting')
  return true
}

// Pide la URL temporal de descarga del fichero generado.
export async function requestDownload(id) {
  const data = await downloadProcessLog(id)
  if (!data || !data.success) throw new Error((data && data.error) || 'error_retrieving_data')
  return data.url
}

// Detalle de error de un informe fallido. Los jobs de informe detallado marcan el fallo
// con `description_status = 'error: <mensaje>'` (GenerateReportBase). Devuelve el mensaje
// (sin el prefijo) o null si no es un error. Puro.
export function errorDetail(row) {
  const ds = ((row && row.description_status) || '').trim()
  const m = /^error\s*:?\s*([\s\S]*)$/i.exec(ds)
  return m ? (m[1] || ds) : null
}

// Clave canónica de estado para agrupar/filtrar: todos los fallos caen bajo 'error'
// (si no, cada error sería una entrada distinta por su mensaje); el resto, su
// description_status. Puro. La vista traduce 'error' con $t('error').
export function statusKey(row) {
  return errorDetail(row) !== null ? 'error' : ((row && row.description_status) || '')
}

// Semana del informe, inferida del `description` del processlog, que el backend genera como
// 'Informe del proyecto <proyecto> - Semana: <week> del <desde> al <hasta>'
// (ApiDashboardController). Devuelve el número de semana (string) o null. Puro.
export function reportWeek(row) {
  const m = /Semana:\s*(\d+)/i.exec((row && row.description) || '')
  return m ? m[1] : null
}

// Título del informe: la parte del `description` antes de ' - Semana:' (p. ej.
// 'Informe del proyecto CSF AURORA'). Si no hay ese patrón, el description completo. Puro.
export function reportTitle(row) {
  const ds = ((row && row.description) || '').trim()
  const i = ds.indexOf(' - Semana')
  return i >= 0 ? ds.slice(0, i).trim() : ds
}

// Periodo (desde/hasta) inferido del mismo `description` ('... del <desde> al <hasta>').
// Devuelve { from, to } o null. Puro.
export function reportPeriod(row) {
  const m = /\bdel\s+(\S+)\s+al\s+(\S+)/i.exec((row && row.description) || '')
  return m ? { from: m[1], to: m[2] } : null
}

// ¿El proceso sigue generándose? Solo entonces tiene sentido el indicador de progreso.
// Terminado = error, Completed (2), No files (3) o progress 100. Puro.
export function isInProgress(row) {
  if (errorDetail(row) !== null) return false
  if (row.status === 2 || row.status === 3) return false
  return (row.progress || 0) < 100
}

// Color (clase Bulma `tag`) del estado. Error → danger; resto según status/progress del
// Processlog (2 = Completed, 3 = No files; 0/1 = Pending/Compressing). La ETIQUETA la pone
// la vista (description_status, o 'Error' si es un fallo) — aquí solo el color.
export function statusVariant(row) {
  if (errorDetail(row) !== null) return 'is-danger'
  if (row.status === 2 || row.progress === 100) return 'is-success'
  if (row.status === 3) return 'is-warning'
  return 'is-info'
}

// Filtra client-side (el backend devuelve todos los registros): por texto libre
// (descripción o usuario), por estado (clave canónica) y por semana. '' = sin filtro. Puro.
export function filterHistory(rows, { text = '', status = '', week = '' } = {}) {
  const q = text.trim().toLowerCase()
  const w = String(week).trim()
  return (rows || []).filter((r) => {
    const matchText = !q
      || (r.description || '').toLowerCase().includes(q)
      || (r.user && r.user.fullname ? r.user.fullname.toLowerCase().includes(q) : false)
    const matchStatus = !status || statusKey(r) === status
    const matchWeek = !w || String(reportWeek(r) || '') === w
    return matchText && matchStatus && matchWeek
  })
}

// Claves de estado distintas presentes (para el desplegable de filtro). Puro.
export function statusOptions(rows) {
  return [...new Set((rows || []).map((r) => statusKey(r)).filter(Boolean))]
}

// Aplica a la lista una actualización de progreso recibida por EventBus('processQueueLog').
// Puro: devuelve una NUEVA lista (reactiva en Vue 2 al reasignar) o la misma si el id no está.
export function applyRealtimeUpdate(history, event) {
  const id = event && event.data && event.data.id
  const index = history.findIndex((h) => h.id == id)
  if (index === -1) return history
  const next = history.slice()
  next[index] = {
    ...next[index],
    progress: event.data.progress,
    description_status: event.data.description_status,
    status: event.data.status,
  }
  return next
}
